import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import driveApi, { errorMessage, ROLES } from '../../api/drive';
import { useDrive } from '../../contexts/DriveProvider';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { FileTag } from './ItemsView';

/**
 * Pick a folder (in this drive or another one) and move the items there.
 * Works without drag and drop, so also to a parent folder and on touch screens.
 */
const MoveDialog = ({ items, onOpenChange }) => {
  const open = !!items?.length;
  const { toast, bump, personalDrive, sharedDrives } = useDrive();
  const [driveId, setDriveId] = useState(null);
  const [folderId, setFolderId] = useState(null);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDriveId(items[0].drive);
      setFolderId(items[0].parent || null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !driveId) return undefined;
    let cancelled = false;
    setView(null);
    driveApi.contents(driveId, folderId)
      .then((res) => { if (!cancelled) setView(res); })
      .catch(() => { if (!cancelled) setView({ error: true, items: [], breadcrumbs: [] }); });
    return () => { cancelled = true; };
  }, [open, driveId, folderId]);

  const moving = new Set((items || []).map((i) => i.id));
  const folders = (view?.items || []).filter((i) => i.kind === 'folder');
  const sameFolder = (items || []).every((i) => i.drive === driveId && (i.parent || null) === folderId);
  const otherDrive = (items || []).some((i) => i.drive !== driveId);
  // Drives you can add to, plus the one the items are in (e.g. someone else's My Drive)
  const drives = [
    ...(personalDrive ? [{ id: personalDrive.id, name: 'My Drive' }] : []),
    ...(sharedDrives || []).filter((d) => d.role >= ROLES.EDITOR).map((d) => ({ id: d.id, name: d.name })),
  ];
  if (items?.length && !drives.some((d) => d.id === items[0].drive)) {
    drives.unshift({ id: items[0].drive, name: items[0].drive_kind === 'personal' ? 'Their My Drive (current)' : `${items[0].drive_name} (current)` });
  }
  const canDrop = view && !view.error && view.role >= ROLES.EDITOR;
  const driveLabel = drives.find((d) => d.id === driveId)?.name || view?.drive?.name;
  const here = view?.folder?.name || driveLabel || '';

  const move = async () => {
    setBusy(true);
    try {
      await Promise.all(items.map((i) => driveApi.move(i.id, folderId, driveId)));
      toast(items.length > 1 ? `Moved ${items.length} items to “${here}”` : `Moved to “${here}”`);
      bump();
      onOpenChange(false);
    } catch (err) {
      toast(errorMessage(err, 'Could not move.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {items?.length > 1 ? `Move ${items.length} items` : `Move “${items?.[0]?.name}”`}
          </DialogTitle>
          <DialogDescription>Choose a drive and a folder.</DialogDescription>
        </DialogHeader>

        {drives.length > 1 && (
          <label className="flex items-center gap-2 text-[13.5px]">
            <span className="text-muted-foreground">Drive</span>
            <select
              value={driveId || ''}
              onChange={(e) => { setDriveId(e.target.value); setFolderId(null); }}
              className="h-9 min-w-0 flex-1 rounded-lg border border-border bg-card px-2"
            >
              {drives.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
        )}

        <nav aria-label="Folder path" className="flex min-w-0 flex-wrap items-center gap-0.5 text-[13.5px] text-muted-foreground">
          {(!view || view.reaches_root !== false) && (
            <button type="button" onClick={() => setFolderId(null)} className="rounded px-1 py-0.5 hover:bg-muted hover:text-foreground">
              {driveLabel || '…'}
            </button>
          )}
          {(view?.breadcrumbs || []).map((c, i) => (
            <React.Fragment key={c.id}>
              {(i > 0 || view.reaches_root !== false) && <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
              <button type="button" onClick={() => setFolderId(c.id)} className="truncate rounded px-1 py-0.5 hover:bg-muted hover:text-foreground">
                {c.name}
              </button>
            </React.Fragment>
          ))}
        </nav>

        <ul aria-label="Folders" className="flex h-64 flex-col overflow-y-auto rounded-lg border border-border p-1">
          {!view && <li className="p-3 text-sm text-muted-foreground">Loading…</li>}
          {view && folders.length === 0 && <li className="p-3 text-sm text-muted-foreground">No folders here.</li>}
          {folders.map((f) => {
            const self = moving.has(f.id);
            return (
              <li key={f.id}>
                <button
                  type="button"
                  disabled={self}
                  onClick={() => setFolderId(f.id)}
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[14px] hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FileTag item={f} />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  {!self && <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>
        {view && !canDrop && !view.error && <p className="text-[13px] text-muted-foreground">You can only view this folder, so you can’t move things into it.</p>}
        {otherDrive && canDrop && (
          <p className="text-[13px] text-muted-foreground">
            Moving to another drive: people with access to {driveLabel} will see it; people who only had access through the old drive won’t. Only managers can do this.
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={move} disabled={!canDrop || sameFolder || busy}>
            {sameFolder ? 'Already here' : `Move to “${here}”`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveDialog;
